// src/pages/Nominal.jsx - Redesigned with Modern Orange-to-Pink Theme
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import ApiService from '../services/apiService';

const Nominal = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchIn, setSearchIn] = useState('B'); // A=Appellant, R=Respondent, B=Both

  useEffect(() => {
    const storedKeyword = localStorage.getItem('SearchHNominal');
    if (storedKeyword) {
      setSearchQuery(storedKeyword);
    }
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchQuery.trim() || isLoading) return;

    setIsLoading(true);
    setError('');

    try {
      console.log('Starting Nominal Search...');
      console.log('Query:', searchQuery);
      console.log('Search In:', searchIn);

      const searchOptions = {
        pageSize: 25,
        page: 1,
        sortBy: 'rele',
        searchIn: searchIn
      };

      const apiResponse = await ApiService.searchNominal(
        { party: searchQuery, searchIn: searchIn },
        searchOptions
      );

      const searchResults = apiResponse.hits || [];
      const totalCount = apiResponse.total || 0;

      console.log(`📊 Processing ${searchResults.length} results from total ${totalCount}`);

      if (searchResults.length === 0) {
        setError('No results found for your search query. Try different keywords.');
        setIsLoading(false);
        return;
      }

      const resultsData = {
        results: apiResponse.hits || [],
        totalCount: apiResponse.total || 0,
        query: searchQuery,
        searchType: 'Nominal Search',
        timestamp: new Date().toISOString(),
        courtsList: apiResponse.courtsList || [],
        yearList: apiResponse.yearList || [],
        searchData: {
          query: searchQuery,
          searchType: 'Nominal Search',
          sortOrder: 'rele',
          searchIn,
          nearWordsDistance: '5'
        }
      };

      console.log('💾 Storing results:', resultsData);
      localStorage.setItem('searchResults', JSON.stringify(resultsData));
      localStorage.setItem('SearchHNominal', searchQuery);
      console.log('🚀 Navigating to results page...');
      navigate('/results');

    } catch (error) {
      console.error('❌ Nominal Search failed:', error);
      setError(error.message || 'Nominal Search failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setError('');
    localStorage.setItem('SearchHNominal', '');
  };

  return (
    <div className="nominal-layout">
      <Navbar />
      <SearchNavigation />

      <div className="nominal-main-content">
        {/* Hero Section */}
        <div className="nominal-hero">
          <div className="hero-badge">
            <i className="bx bx-user-circle"></i>
            <span>Nominal Search</span>
          </div>
          <h1 className="hero-title">
            Find Cases by Party Names
          </h1>
          <p className="hero-subtitle">
            Search for legal cases using appellant or respondent names
          </p>
        </div>

        <div className="nominal-search-container">
          {/* Error Alert */}
          {error && (
            <div className="error-alert">
              <i className="bx bx-error-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Search Card */}
          <div className="nominal-card">
            <div className="card-header-nominal">
              <div className="header-icon-wrapper">
                <i className="bx bx-search-alt-2"></i>
              </div>
              <div className="header-content">
                <h2 className="card-title-nominal">Party Name Search</h2>
                <p className="card-subtitle-nominal">Enter full or partial party name</p>
              </div>
            </div>

            <div className="card-body-nominal">
              {/* Search Form */}
              <form onSubmit={handleSearch} className="search-form-nominal">
                <div className="search-input-group">
                  <div className="input-icon-left">
                    <i className="bx bx-user"></i>
                  </div>
                  <input
                    type="text"
                    className="search-input-nominal"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Type party name (e.g., State of Maharashtra, John Doe)"
                    disabled={isLoading}
                  />
                  {searchQuery.length > 0 && (
                    <button
                      type="button"
                      className="clear-btn-nominal"
                      onClick={handleClearSearch}
                      disabled={isLoading}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  )}
                  <button
                    type="submit"
                    className="submit-btn-nominal"
                    disabled={isLoading || !searchQuery.trim()}
                  >
                    {isLoading ? (
                      <i className="bx bx-loader-alt bx-spin"></i>
                    ) : (
                      <i className="bx bx-search"></i>
                    )}
                    <span>{isLoading ? 'Searching...' : 'Search'}</span>
                  </button>
                </div>

                {/* Search Options */}
                <div className="search-options-nominal">
                  <div className="options-label">
                    <i className="bx bx-filter"></i>
                    <span>Search in:</span>
                  </div>
                  <div className="radio-group-nominal">
                    <label className="radio-card-nominal">
                      <input
                        type="radio"
                        name="searchIn"
                        value="A"
                        checked={searchIn === 'A'}
                        onChange={(e) => setSearchIn(e.target.value)}
                      />
                      <span className="radio-checkmark"></span>
                      <div className="radio-content">
                        <i className="bx bx-user-voice"></i>
                        <span className="radio-label">Appellant</span>
                      </div>
                    </label>

                    <label className="radio-card-nominal">
                      <input
                        type="radio"
                        name="searchIn"
                        value="R"
                        checked={searchIn === 'R'}
                        onChange={(e) => setSearchIn(e.target.value)}
                      />
                      <span className="radio-checkmark"></span>
                      <div className="radio-content">
                        <i className="bx bx-user-check"></i>
                        <span className="radio-label">Respondent</span>
                      </div>
                    </label>

                    <label className="radio-card-nominal">
                      <input
                        type="radio"
                        name="searchIn"
                        value="B"
                        checked={searchIn === 'B'}
                        onChange={(e) => setSearchIn(e.target.value)}
                      />
                      <span className="radio-checkmark"></span>
                      <div className="radio-content">
                        <i className="bx bx-group"></i>
                        <span className="radio-label">Both</span>
                      </div>
                    </label>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Info Cards */}
          <div className="info-cards-nominal">
            <div className="info-card-nominal">
              <div className="info-icon-nominal">
                <i className="bx bx-info-circle"></i>
              </div>
              <div className="info-content-nominal">
                <h3>Quick Search</h3>
                <p>Enter full or partial party names to find relevant cases instantly.</p>
              </div>
            </div>

            <div className="info-card-nominal">
              <div className="info-icon-nominal">
                <i className="bx bx-bullseye"></i>
              </div>
              <div className="info-content-nominal">
                <h3>Precise Results</h3>
                <p>Filter by appellant, respondent, or search both parties at once.</p>
              </div>
            </div>

            <div className="info-card-nominal">
              <div className="info-icon-nominal">
                <i className="bx bx-lightning-charge"></i>
              </div>
              <div className="info-content-nominal">
                <h3>Fast & Accurate</h3>
                <p>Get comprehensive results from our extensive legal database.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ============================================
           BASE LAYOUT
        ============================================ */
        .nominal-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .nominal-main-content {
          padding-top: 140px;
          width: 100%;
          min-height: 100vh;
        }

        /* ============================================
           HERO SECTION
        ============================================ */
        .nominal-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 60px 20px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 20px;
        }

        .hero-badge i {
          font-size: 18px;
        }

        .hero-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin: 0 0 12px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.1rem;
          opacity: 0.95;
          margin: 0;
          font-weight: 400;
        }

        /* ============================================
           SEARCH CONTAINER
        ============================================ */
        .nominal-search-container {
          max-width: 900px;
          margin: -40px auto 0;
          padding: 0 20px 60px;
          position: relative;
          z-index: 10;
        }

        /* Error Alert */
        .error-alert {
          background: linear-gradient(135deg, #fee, #fdd);
          border: 1px solid #fcc;
          color: #c33;
          padding: 14px 20px;
          border-radius: 12px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          box-shadow: 0 2px 8px rgba(220, 53, 69, 0.15);
        }

        .error-alert i {
          font-size: 20px;
          flex-shrink: 0;
        }

        /* ============================================
           NOMINAL CARD
        ============================================ */
        .nominal-card {
          background: white;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          margin-bottom: 30px;
        }

        .card-header-nominal {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          padding: 30px 35px;
          display: flex;
          align-items: center;
          gap: 20px;
          border-bottom: 2px solid #f5f5f5;
        }

        .header-icon-wrapper {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
          flex-shrink: 0;
          box-shadow: 0 4px 15px rgba(245, 166, 35, 0.3);
        }

        .header-content {
          flex: 1;
        }

        .card-title-nominal {
          font-size: 24px;
          font-weight: 700;
          color: #333;
          margin: 0 0 5px 0;
        }

        .card-subtitle-nominal {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        .card-body-nominal {
          padding: 40px 35px;
        }

        /* ============================================
           SEARCH FORM
        ============================================ */
        .search-form-nominal {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }

        .search-input-group {
          position: relative;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .input-icon-left {
          position: absolute;
          left: 18px;
          color: #999;
          font-size: 22px;
          z-index: 1;
          pointer-events: none;
        }

        .search-input-nominal {
          flex: 1;
          padding: 16px 50px 16px 55px;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          font-size: 15px;
          color: #333;
          background: #fafafa;
          transition: all 0.3s ease;
        }

        .search-input-nominal:focus {
          outline: none;
          border-color: #E91E63;
          background: white;
          box-shadow: 0 0 0 4px rgba(245, 166, 35, 0.1);
        }

        .search-input-nominal:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .clear-btn-nominal {
          position: absolute;
          right: 150px;
          width: 32px;
          height: 32px;
          border: none;
          background: #f5f5f5;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #666;
          transition: all 0.2s ease;
        }

        .clear-btn-nominal:hover:not(:disabled) {
          background: #e0e0e0;
          color: #333;
        }

        .clear-btn-nominal i {
          font-size: 20px;
        }

        .submit-btn-nominal {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 16px 32px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
          box-shadow: 0 4px 20px rgba(245, 166, 35, 0.3);
        }

        .submit-btn-nominal:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(233, 30, 99, 0.4);
        }

        .submit-btn-nominal:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .submit-btn-nominal i {
          font-size: 20px;
        }

        /* ============================================
           SEARCH OPTIONS
        ============================================ */
        .search-options-nominal {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .options-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 15px;
          font-weight: 700;
          color: #555;
        }

        .options-label i {
          font-size: 18px;
          color: #E91E63;
        }

        .radio-group-nominal {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .radio-card-nominal {
          position: relative;
          display: flex;
          align-items: center;
          padding: 16px 20px;
          background: #fafafa;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          user-select: none;
        }

        .radio-card-nominal:hover {
          border-color: #E91E63;
          background: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(245, 166, 35, 0.15);
        }

        .radio-card-nominal input[type="radio"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .radio-checkmark {
          width: 20px;
          height: 20px;
          border: 2px solid #ddd;
          border-radius: 50%;
          background: white;
          transition: all 0.3s ease;
          flex-shrink: 0;
          position: relative;
          margin-right: 12px;
        }

        .radio-card-nominal:hover .radio-checkmark {
          border-color: #E91E63;
        }

        .radio-card-nominal input[type="radio"]:checked ~ .radio-checkmark {
          border-color: #c41849;
          background: linear-gradient(135deg, #E91E63, #c41849);
        }

        .radio-card-nominal input[type="radio"]:checked ~ .radio-checkmark::after {
          content: '';
          position: absolute;
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          top: 4px;
          left: 4px;
        }

        .radio-card-nominal input[type="radio"]:checked ~ .radio-content {
          color: #c41849;
        }

        .radio-card-nominal input[type="radio"]:checked {
          & ~ * {
            font-weight: 700;
          }
        }

        .radio-card-nominal:has(input[type="radio"]:checked) {
          border-color: #c41849;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.05), rgba(233, 30, 99, 0.05));
          box-shadow: 0 4px 15px rgba(233, 30, 99, 0.15);
        }

        .radio-content {
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s ease;
        }

        .radio-content i {
          font-size: 22px;
          color: #E91E63;
        }

        .radio-label {
          font-size: 15px;
          font-weight: 600;
          color: #555;
        }

        /* ============================================
           INFO CARDS
        ============================================ */
        .info-cards-nominal {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .info-card-nominal {
          background: white;
          border-radius: 16px;
          padding: 24px;
          display: flex;
          gap: 16px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .info-card-nominal:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(245, 166, 35, 0.15);
        }

        .info-icon-nominal {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.15), rgba(233, 30, 99, 0.1));
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #E91E63;
          font-size: 24px;
          flex-shrink: 0;
        }

        .info-content-nominal h3 {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          margin: 0 0 8px 0;
        }

        .info-content-nominal p {
          font-size: 14px;
          color: #666;
          margin: 0;
          line-height: 1.5;
        }

        /* ============================================
           ANIMATIONS
        ============================================ */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .bx-spin {
          animation: spin 1s linear infinite;
        }

        /* ============================================
           RESPONSIVE DESIGN
        ============================================ */
        @media (max-width: 768px) {
          .nominal-main-content {
            padding-top: 130px;
          }

          .nominal-hero {
            padding: 40px 15px;
          }

          .hero-title {
            font-size: 1.8rem;
          }

          .hero-subtitle {
            font-size: 0.95rem;
          }

          .nominal-search-container {
            margin-top: -30px;
            padding: 0 15px 40px;
          }

          .card-header-nominal {
            padding: 20px;
            flex-direction: column;
            text-align: center;
          }

          .header-icon-wrapper {
            width: 50px;
            height: 50px;
            font-size: 24px;
          }

          .card-title-nominal {
            font-size: 20px;
          }

          .card-body-nominal {
            padding: 24px 20px;
          }

          .search-input-group {
            flex-direction: column;
          }

          .clear-btn-nominal {
            right: 18px;
            top: 50%;
            transform: translateY(-50%);
          }

          .submit-btn-nominal {
            width: 100%;
            justify-content: center;
            padding: 14px 24px;
          }

          .radio-group-nominal {
            grid-template-columns: 1fr;
          }

          .info-cards-nominal {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.5rem;
          }

          .hero-badge {
            padding: 8px 16px;
            font-size: 13px;
          }

          .card-header-nominal {
            padding: 16px;
          }

          .card-body-nominal {
            padding: 20px 16px;
          }

          .search-input-nominal {
            padding: 14px 50px 14px 50px;
            font-size: 14px;
          }

          .submit-btn-nominal {
            padding: 12px 20px;
            font-size: 15px;
          }

          .radio-card-nominal {
            padding: 12px 16px;
          }

          .radio-label {
            font-size: 14px;
          }
        }
               /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Nominal;